import { useMemo } from 'react';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { enableTeamsOfTeams, memberFilterPageSize, teamsListPageSize } from '@gonfalon/dogfood-flags/dogfood-flags';
import { memberDisplayName } from '@gonfalon/members/memberFirstName';
import { memberInitials } from '@gonfalon/members/memberInitials';
import type { Member } from '@gonfalon/members/types';
import { getMemberQuery, getMembersQuery } from '@gonfalon/rest-api/queries/members';
import { teamsDetail, teamsList } from '@gonfalon/rest-api/queries/teams';
import { conditionalSuspenseQuery } from '@gonfalon/suspense/conditionalSuspenseQuery';
import { isSuspenseQueryEnabled } from '@gonfalon/suspense/isSuspenseQueryEnabled';
import { keepPreviousData, useQueries, useQuery, useSuspenseQueries } from '@tanstack/react-query';
import nullthrows from 'nullthrows';

import type { MaintainerComboBoxItem, MemberItem, ResourceSpec, TeamItem } from './types';

export interface UseMaintainerItemsParams {
  maintainerId?: string;
  maintainerIds?: string[];
  maintainerTeamKey?: string;
  maintainerTeamKeys?: string[];
  teamsOnly?: boolean;
  filterText: string;
  membersOnly?: boolean;
  excludeEmptyTeams?: boolean;
  accessChecks?: {
    members?: ResourceSpec;
    teams?: {
      accessCheck: 'reviewApprovalRequest';
      accessCheckResource: string;
    };
  };
  filterOutCurrentMember?: boolean;
  filterMembers?: (member: Member) => boolean;
  maxTeams?: number;
  maxMembers?: number;
}

/**
 * V4 only accepts the `accessCheck` filter alongside an `id` filter, so the access check runs as a
 * second query over the ids of the page the text search returned. It is a non-suspense query in both
 * variants: it depends on the page's data, and a failed check should not crash the dialog.
 */
function useAccessCheckedMembers({
  page,
  accessCheck,
  enabled,
}: {
  page: { items?: Member[] } | undefined;
  accessCheck: ResourceSpec | undefined;
  enabled: boolean;
}) {
  const ids = useMemo(() => (page?.items ?? []).map((member) => member._id), [page?.items]);
  const narrowed = useQuery({
    ...getMembersQuery({
      query: { limit: memberFilterPageSize(), sort: 'displayName', filter: { ids, accessCheck } },
    }),
    placeholderData: keepPreviousData,
    enabled: enabled && accessCheck !== undefined && ids.length > 0,
  });
  const members = useMemo(() => {
    if (accessCheck === undefined) {
      return page;
    }
    // While this page's check is in flight, `narrowed.data` is undefined or the previous search's
    // placeholder. Intersecting with the current page means a row never shows before its check
    // passes and a stale row never outlives its search.
    const checked = new Set((narrowed.data?.items ?? []).map((member) => member._id));
    return { ...page, items: (page?.items ?? []).filter((member) => checked.has(member._id)) };
  }, [accessCheck, page, narrowed.data]);
  return { members, isFetching: narrowed.isFetching };
}

function useSuspenseMembers({
  maintainerId,
  maintainerIds,
  maintainerTeamKey,
  maintainerTeamKeys,
  filterText,
  membersOnly,
  excludeEmptyTeams,
  accessChecks,
  enabled = true,
}: UseMaintainerItemsParams & {
  enabled?: boolean;
}) {
  const allMaintainerIds = Array.from(new Set((maintainerIds ?? []).concat(maintainerId ? [maintainerId] : [])));
  const allMaintainerTeamKeys = Array.from(
    new Set((maintainerTeamKeys ?? []).concat(maintainerTeamKey ? [maintainerTeamKey] : [])),
  );

  const [members, teams] = useSuspenseQueries({
    queries: [
      conditionalSuspenseQuery({
        query: getMembersQuery({
          query: { limit: memberFilterPageSize(), sort: 'displayName', filter: { query: filterText } },
        }),
        enabled,
      }),
      conditionalSuspenseQuery({
        query: teamsList({
          params: {
            limit: teamsListPageSize(),
            filter: {
              query: filterText,
              nomembers: excludeEmptyTeams ? false : undefined,
              accessCheckResource: accessChecks?.teams?.accessCheckResource,
              accessCheck: accessChecks?.teams?.accessCheck,
            },
          },
        }),
        enabled: enableTeamsOfTeams() && !membersOnly && enabled,
      }),
    ],
  });

  const accessChecked = useAccessCheckedMembers({
    page: isSuspenseQueryEnabled(members) ? members.data : undefined,
    accessCheck: accessChecks?.members,
    enabled,
  });

  // Use non-suspense queries for per-id resolution so a forbidden lookup on a
  // single inaccessible maintainer team/member doesn't crash the dialog.
  const maintainerIdsQueries = useQueries({
    queries: enabled ? allMaintainerIds.map((id) => getMemberQuery({ id })) : [],
  });

  const maintainerTeamKeysQueries = useQueries({
    queries: allMaintainerTeamKeys.map((teamKey) => ({
      ...teamsDetail({ teamKey }),
      enabled: enableTeamsOfTeams() && !membersOnly && enabled,
    })),
  });

  const maintainerMembers = useMemo(
    () => maintainerIdsQueries.map((q) => q.data).filter((m) => m !== undefined),
    [maintainerIdsQueries],
  );

  const maintainerTeams = useMemo(
    () => maintainerTeamKeysQueries.map((q) => q.data).filter((t) => t !== undefined),
    [maintainerTeamKeysQueries],
  );

  return {
    members: accessChecked.members,
    teams: isSuspenseQueryEnabled(teams) ? teams.data : undefined,
    maintainerMembers,
    maintainerTeams,
    allMaintainerIds,
    allMaintainerTeamKeys,
    isPending:
      members.isFetching ||
      accessChecked.isFetching ||
      teams.isFetching ||
      maintainerIdsQueries.some((q) => q.isFetching) ||
      maintainerTeamKeysQueries.some((q) => q.isFetching),
  };
}

function useMembers({
  maintainerId,
  maintainerIds,
  maintainerTeamKey,
  maintainerTeamKeys,
  filterText,
  membersOnly,
  excludeEmptyTeams,
  accessChecks,
  enabled = true,
}: UseMaintainerItemsParams & {
  enabled?: boolean;
}) {
  const allMaintainerIds = Array.from(new Set((maintainerIds ?? []).concat(maintainerId ? [maintainerId] : [])));
  const allMaintainerTeamKeys = Array.from(
    new Set((maintainerTeamKeys ?? []).concat(maintainerTeamKey ? [maintainerTeamKey] : [])),
  );

  const members = useQuery({
    ...getMembersQuery({
      query: { limit: memberFilterPageSize(), sort: 'displayName', filter: { query: filterText } },
    }),
    placeholderData: keepPreviousData,
    enabled,
  });
  const accessChecked = useAccessCheckedMembers({
    page: members.data,
    accessCheck: accessChecks?.members,
    enabled,
  });
  const teams = useQuery({
    ...teamsList({
      params: {
        limit: teamsListPageSize(),
        filter: {
          query: filterText,
          nomembers: excludeEmptyTeams ? false : undefined,
          accessCheckResource: accessChecks?.teams?.accessCheckResource,
          accessCheck: accessChecks?.teams?.accessCheck,
        },
      },
    }),
    placeholderData: keepPreviousData,
    enabled: enableTeamsOfTeams() && !membersOnly && enabled,
  });

  const maintainerIdsQueries = useQueries({
    queries: enabled
      ? allMaintainerIds.map((id) => ({
          ...getMemberQuery({ id }),
        }))
      : [],
  });

  const maintainerTeamKeysQueries = useQueries({
    queries: allMaintainerTeamKeys.map((teamKey) => ({
      ...teamsDetail({ teamKey }),
      placeholderData: keepPreviousData,
      enabled: enableTeamsOfTeams() && !membersOnly && enabled,
    })),
  });

  const maintainerMembers = useMemo(
    () => maintainerIdsQueries.map((q) => q.data).filter((m) => m !== undefined),
    [maintainerIdsQueries],
  );

  const maintainerTeams = useMemo(
    () => maintainerTeamKeysQueries.map((q) => q.data).filter((t) => t !== undefined),
    [maintainerTeamKeysQueries],
  );
  return {
    members: accessChecked.members,
    teams: teams.data,
    maintainerMembers,
    maintainerTeams,
    allMaintainerIds,
    allMaintainerTeamKeys,
    isPending:
      members.isFetching ||
      accessChecked.isFetching ||
      teams.isFetching ||
      maintainerIdsQueries.some((q) => q.isFetching) ||
      maintainerTeamKeysQueries.some((q) => q.isFetching),
  };
}

type UseMaintainerComboBoxItemParams = UseMaintainerItemsParams &
  ReturnType<typeof useMembers | typeof useSuspenseMembers>;

function useMaintainerComboBoxItem({
  members,
  teams,
  maintainerMembers,
  maintainerTeams,
  allMaintainerIds,
  allMaintainerTeamKeys,
  filterText,
  filterOutCurrentMember,
  teamsOnly,
  maintainerId,
  membersOnly,
  filterMembers,
  maxTeams = 4,
  maxMembers = 4,
}: UseMaintainerComboBoxItemParams) {
  const { profile } = useAccountContext();
  const isFiltered = filterText !== undefined && filterText !== '';

  const selectedItems = useMemo(() => {
    // Build selected items first
    const _selectedItems: Array<MemberItem | TeamItem> = [];

    // Add selected members to selected section (multi-selection)
    if (maintainerMembers.length > 0) {
      for (const member of maintainerMembers) {
        const name = memberDisplayName(member);
        if (filterOutCurrentMember && member._id === profile._id) {
          continue;
        }
        _selectedItems.push({
          type: 'member',
          id: member._id,
          name,
          email: member.email,
          initials: memberInitials(member),
          description: member.email !== name ? member.email : undefined,
          resource: member,
        });
      }
    }

    // Add selected teams to selected section (multi-selection)
    if (maintainerTeams.length > 0) {
      for (const team of maintainerTeams) {
        if (!team || typeof team !== 'object' || !('key' in team) || !('name' in team)) {
          continue;
        }
        _selectedItems.push({
          type: 'team',
          id: nullthrows(team.key),
          name: nullthrows(team.name),
          resource: team,
        });
      }
    }
    return _selectedItems;
  }, [maintainerMembers, maintainerTeams, profile._id, filterOutCurrentMember]);

  const suggestedItems = useMemo(() => {
    // Build suggested items (excluding selected items)
    const _suggestedItems: Array<MemberItem | TeamItem> = [];

    // Only add profile to suggested if not already selected
    if (!teamsOnly && (maintainerId === undefined || profile._id !== maintainerId)) {
      const isProfileSelected = allMaintainerIds.includes(profile._id);
      if (!isProfileSelected && !filterOutCurrentMember && (!filterMembers || filterMembers(profile))) {
        const profileName = memberDisplayName(profile);
        _suggestedItems.push({
          type: 'member',
          id: profile._id,
          name: profileName,
          email: profile.email,
          initials: memberInitials(profile),
          description: profile.email !== profileName ? profile.email : undefined,
          resource: profile,
        });
      }
    }

    if (profile.teams !== undefined && profile.teams.length > 0 && !membersOnly) {
      for (const team of profile.teams) {
        const isTeamSelected = allMaintainerTeamKeys.includes(team.key);
        if (!isTeamSelected) {
          _suggestedItems.push({
            type: 'team',
            id: team.key,
            name: team.name,
            resource: team,
          });
        }
      }
    }

    return _suggestedItems;
  }, [
    profile,
    membersOnly,
    allMaintainerIds,
    allMaintainerTeamKeys,
    maintainerId,
    filterOutCurrentMember,
    filterMembers,
    teamsOnly,
  ]);

  // React Aria keys every list item by its raw id, and those keys must be unique
  // across the entire listbox (not per section). An entity that appears in both
  // the "Suggested" section and the "Teams"/"Members" section would collide,
  // tangling the collection so rows render under the wrong header. The suggested
  // section is only shown when unfiltered, so only de-dupe in that case.
  const suggestedMemberIds = useMemo(
    () =>
      isFiltered ? new Set<string>() : new Set(suggestedItems.filter((i) => i.type === 'member').map((i) => i.id)),
    [suggestedItems, isFiltered],
  );
  const suggestedTeamKeys = useMemo(
    () => (isFiltered ? new Set<string>() : new Set(suggestedItems.filter((i) => i.type === 'team').map((i) => i.id))),
    [suggestedItems, isFiltered],
  );

  const memberItems = useMemo(() => {
    // Build member items (excluding selected members)
    const _memberItems: MemberItem[] = [];
    for (const member of members?.items ?? []) {
      // Skip if this member is already selected
      if (allMaintainerIds.includes(member._id)) {
        continue;
      }

      // Skip if this is the single maintainer member
      if (member._id === maintainerId) {
        continue;
      }

      // Skip if this is the current member and filterOutCurrentMember is true
      if (filterOutCurrentMember && member._id === profile._id) {
        continue;
      }

      // Skip if this member is already shown in the "Suggested" section
      if (suggestedMemberIds.has(member._id)) {
        continue;
      }

      // Skip if the caller-provided predicate excludes this member
      if (filterMembers && !filterMembers(member)) {
        continue;
      }

      const name = memberDisplayName(member);
      _memberItems.push({
        type: 'member',
        id: member._id,
        name,
        email: member.email,
        initials: memberInitials(member),
        description: member.email !== name ? member.email : undefined,
        resource: member,
      });
    }

    return _memberItems;
  }, [
    members?.items,
    allMaintainerIds,
    filterOutCurrentMember,
    profile._id,
    maintainerId,
    suggestedMemberIds,
    filterMembers,
  ]);

  const teamItems = useMemo(() => {
    // Build team items (excluding selected teams)
    const _teamItems: TeamItem[] = [];
    if (teams?.items && !membersOnly) {
      for (const team of teams.items ?? []) {
        // Skip if this team is already selected
        const teamKey = nullthrows(team.key);
        if (allMaintainerTeamKeys.includes(teamKey)) {
          continue;
        }

        // Skip if this team is already shown in the "Suggested" section
        if (suggestedTeamKeys.has(teamKey)) {
          continue;
        }

        const teamName = nullthrows(team.name);
        const totalCount = team.members?.totalCount ?? 0;

        _teamItems.push({
          type: 'team',
          id: teamKey,
          name: teamName,
          resource: team,
          teamCount: totalCount,
        });
      }
    }

    return _teamItems;
  }, [teams?.items, allMaintainerTeamKeys, membersOnly, suggestedTeamKeys]);

  return useMemo(() => {
    const itemsInSections: MaintainerComboBoxItem[] = [];

    // Selected items are not matched against the filter, so keep the section out of a
    // filtered collection — otherwise it shadows the listbox empty state.
    if (selectedItems.length > 0 && !isFiltered) {
      itemsInSections.push({ id: 'selected', name: 'Selected', children: selectedItems });
    }

    // Add suggested section (if not filtered and there are suggested items)
    if (!isFiltered && suggestedItems.length > 0) {
      itemsInSections.push({ id: 'suggested', name: 'Suggested', children: suggestedItems });
    }

    // Add teams section (if there are team items and not members only)
    const visibleTeams = maxTeams !== undefined ? teamItems.slice(0, maxTeams) : teamItems;
    if (visibleTeams.length > 0 && !membersOnly) {
      itemsInSections.push({ id: 'teams', name: 'Teams', children: visibleTeams });
    }

    // Add members section (if there are member items and not teams only)
    const visibleMembers = maxMembers !== undefined ? memberItems.slice(0, maxMembers) : memberItems;
    if (visibleMembers.length > 0 && !teamsOnly) {
      itemsInSections.push({ id: 'members', name: 'Members', children: visibleMembers });
    }

    return itemsInSections;
  }, [selectedItems, suggestedItems, teamItems, memberItems, isFiltered, teamsOnly, membersOnly, maxTeams, maxMembers]);
}

export function useMaintainerItems(
  params: UseMaintainerItemsParams & {
    suspense?: boolean;
  },
) {
  const { suspense = true, ...rest } = params;
  const suspendedItems = useSuspenseMembers({
    ...rest,
    enabled: suspense,
  });
  const items = useMembers({
    ...rest,
    enabled: !suspense,
  });
  const itemsInSections = useMaintainerComboBoxItem({
    ...rest,
    ...(suspense ? suspendedItems : items),
  });
  return {
    itemsInSections,
    isPending: suspense ? suspendedItems.isPending : items.isPending,
  };
}
